
  import { ComponentPublicInstance, computed, defineComponent, nextTick, PropType, ref } from 'vue';
  import { useVirtualizer } from '@tanstack/vue-virtual';
  import type { ValidationRule } from '@vuelidate/core';
  import useVuelidate from '@vuelidate/core';
  import { requiredIf } from '@vuelidate/validators';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import { TBivarianceHack } from '@/common/types/bivarianceHack';
  import { TSelectOption } from '@/common/types/select';

  import CombinedContextMenu from './CombinedContextMenu.vue';
  import GlobalContextMenu from './GlobalContextMenu.vue';
  import LocalContextMenu from './LocalContextMenu.vue';
  import TreeMultiselectOption from './TreeMultiselectOption.vue';
  import type { TAdditionalMenuConfig, TProcessedTreeOption, TRawTreeOption, TTreeOption } from './types';
  import { GlobalContextMenuActions, LocalContextMenuActions, MenuModes } from './types';

  const DEFAULT_ROW_HEIGHT_PX = 55;
  const VIRTUAL_OVERSCAN_COUNT = 20;
  const SEARCH_DEBOUNCE_DELAY_MS = 500;

  type ContextMenu = {
    isVisible: boolean;
    position: { left: number; top: number };
  };
  type TComponentData = {
    selectedIds: (string | number)[];
    flatMapByValue: Record<string, TProcessedTreeOption>;
    isOpen: boolean;
    dropdownState: {
      isDropdownUp: boolean;
      dropdownPositionUpdateRafId: number | null;
    };
    searchQuery: string;
    searchTimeout: number | null;
    searchWorker: Worker | null;
    matchedSearchIds: Set<string | number> | null;
    flatSearchItems: Array<{ value: string | number; label: string; level: number }>;
    expandedItems: Set<string>;
    flattenedOptions: TProcessedTreeOption[];
    filteredOptions: TProcessedTreeOption[];
    localContextMenu: ContextMenu & { selectedItem: TProcessedTreeOption | null };
    globalContextMenu: ContextMenu;
    combinedContextMenu: ContextMenu;
    shiftRangeAnchorValue: string | number | null;
  };

  export default defineComponent({
    name: 'TreeMultiselect',
    components: {
      TreeMultiselectOption,
      LocalContextMenu,
      ButtonComponent,
      GlobalContextMenu,
      CombinedContextMenu,
    },
    emits: ['update:modelValue'],
    props: {
      options: {
        type: Array as PropType<TRawTreeOption[]>,
        default: () => [],
      },
      modelValue: {
        type: [Array, String] as PropType<(number | string)[]>,
        default: () => [],
      },
      required: {
        type: Boolean,
        default: false,
      },
      isDirtyValidation: {
        type: Boolean,
        default: false,
      },
      menuMode: {
        type: String as PropType<MenuModes>,
        default: MenuModes.ACTION_AND_LOCAL,
        validator: (value: string) => Object.values(MenuModes).includes(value as MenuModes),
      },
      levelEventsLabels: {
        type: Array as PropType<readonly string[]>,
        default: () => [],
      },
      customValidations: {
        type: Object as PropType<Record<string, ValidationRule>>,
        default: () => ({}),
      },
      textError: {
        type: String,
        default: 'Поле обязательно для заполнения',
      },
      valueProp: {
        type: String,
        default: 'value',
      },
      labelProp: {
        type: String,
        default: 'label',
      },
      childrenProp: {
        type: String,
        default: 'options',
      },
      placeholder: {
        type: String,
        default: 'Выберите',
      },
      isSingleMode: {
        type: Boolean,
        default: false,
      },
      noResultsText: {
        type: String,
        default: 'Ничего не найдено.',
      },
      noOptionsText: {
        type: String,
        default: 'Список пуст.',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      dropdownMaxHeight: {
        type: Number,
        default: 400,
      },
      getLabel: {
        type: Function as PropType<TBivarianceHack<[item: TRawTreeOption], string> | undefined>,
      },
    },
    data(): TComponentData {
      return {
        selectedIds: [],
        flatMapByValue: Object.create(null),
        isOpen: false,
        dropdownState: {
          isDropdownUp: false,
          dropdownPositionUpdateRafId: null,
        },
        searchQuery: '',
        searchTimeout: null,
        searchWorker: null,
        matchedSearchIds: null,
        flatSearchItems: [],
        expandedItems: new Set<string>(),
        flattenedOptions: [],
        filteredOptions: [],
        localContextMenu: {
          isVisible: false,
          position: { left: 0, top: 0 },
          selectedItem: null,
        },
        globalContextMenu: {
          isVisible: false,
          position: { left: 0, top: 0 },
        },
        combinedContextMenu: {
          isVisible: false,
          position: { left: 0, top: 0 },
        },
        shiftRangeAnchorValue: null,
      };
    },
    mounted() {
      this.processOptions();
      this.addClickOutsideListener();
    },
    beforeUnmount() {
      this.removeClickOutsideListener();
      this.removeDropdownPositionListeners();

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchWorker?.terminate();
    },
    setup() {
      const v$ = useVuelidate();
      const virtualizerCount = ref(0);
      const listContainer = ref<HTMLElement | null>(null);
      const measuredHeightsMap = new Map<string, number>();
      const visibleOptionKeys = ref<Array<string>>([]);

      const measureElementFn = (el: HTMLElement) => el.getBoundingClientRect().height;

      const getDynamicEstimate = (index: number) => {
        const key = visibleOptionKeys.value[index];
        const measured = key ? measuredHeightsMap.get(key) : undefined;
        if (measured !== undefined) return measured;

        const measuredValues = Array.from(measuredHeightsMap.values());
        if (measuredValues.length) {
          return measuredValues.reduce((sum, value) => sum + value, 0) / measuredValues.length;
        }

        return DEFAULT_ROW_HEIGHT_PX;
      };

      const rowVirtualizerOptions = computed(() => ({
        count: virtualizerCount.value,
        getScrollElement: () => listContainer.value,
        estimateSize: (index: number) => getDynamicEstimate(index),
        getItemKey: (index: number) => visibleOptionKeys.value[index] ?? index,
        measureElement: measureElementFn,
        overscan: VIRTUAL_OVERSCAN_COUNT,
      }));

      const rowVirtualizer = useVirtualizer(rowVirtualizerOptions);

      return {
        v$,
        rowVirtualizer,
        virtualizerCount,
        listContainer,
        visibleOptionKeys,
        measuredHeightsMap,
      };
    },
    validations() {
      return {
        modelValue: {
          required: requiredIf(this.required),
          $autoDirty: this.isDirtyValidation,
          ...this.customValidations,
        },
      };
    },
    methods: {
      buildVisibleOptions(items: TProcessedTreeOption[], result: TProcessedTreeOption[] = []): TProcessedTreeOption[] {
        items.forEach((item) => {
          result.push(item);

          if (this.expandedItems.has(this.getItemKey(item)) && item.children && item.children.length > 0) {
            this.buildVisibleOptions(item.children, result);
          }
        });

        return result;
      },

      initSearchWorker() {
        if (this.searchWorker) return;

        this.searchWorker = new Worker('/workers/searchWorker.js');
        this.searchWorker.onmessage = (e: MessageEvent) => {
          const result = Array.isArray(e.data.result) ? e.data.result : [];
          this.matchedSearchIds = new Set<string | number>(
            result.map((item: { value: string | number }) => item.value),
          );

          this.filteredOptions = this.filterLevel(this.flattenedOptions, '');
          this.expandedItems.clear();
          this.expandItemWithDescendants(this.filteredOptions);
        };
      },

      runSearch(searchTerm: string) {
        const term = searchTerm.trim();

        if (!term) {
          this.matchedSearchIds = null;
          this.filteredOptions = this.flattenedOptions;
          return;
        }

        this.initSearchWorker();

        const itemsForSearch = JSON.parse(JSON.stringify(this.flatSearchItems));

        this.searchWorker?.postMessage({
          items: itemsForSearch,
          searchTerm: term,
          textKeys: ['label'],
          keyField: 'value',
        });
      },

      prepareOptions(rawOptions: TRawTreeOption[]): TTreeOption[] {
        return rawOptions.map((item) => {
          const children = item[this.childrenProp];
          return {
            value: item[this.valueProp],
            label: this.getLabel ? this.getLabel(item) : item[this.labelProp],
            options: Array.isArray(children) ? this.prepareOptions(children) : undefined,
          };
        });
      },

      processOptions() {
        this.filteredOptions = this.flattenedOptions = this.processLevel(this.getPreparedOptions, 1);

        const flat: Array<{ value: string | number; label: string; level: number }> = [];
        const mapByValue: Record<string, TProcessedTreeOption> = Object.create(null);

        const traverse = (items: TProcessedTreeOption[]) => {
          items.forEach((item) => {
            flat.push({
              value: item.value,
              label: item.label,
              level: item.level,
            });
            mapByValue[String(item.value)] = item;
            if (item.children && item.children.length > 0) {
              traverse(item.children);
            }
          });
        };

        traverse(this.flattenedOptions);
        this.flatSearchItems = flat;
        this.flatMapByValue = mapByValue;
      },

      processLevel(items: TTreeOption[], level: number): TProcessedTreeOption[] {
        return items.map((item) => {
          const processedItem: TProcessedTreeOption = {
            ...item,
            level,
            hasChildren: Boolean(item.options && item.options.length > 0),
            children: item.options ? this.processLevel(item.options, level + 1) : [],
          };
          return processedItem;
        });
      },

      toggleDropdown() {
        this.isOpen = !this.isOpen;
      },

      addDropdownPositionListeners() {
        window.addEventListener('resize', this.handleDropdownPositionUpdate);
        document.addEventListener('scroll', this.handleDropdownPositionUpdate, true);
      },

      removeDropdownPositionListeners() {
        window.removeEventListener('resize', this.handleDropdownPositionUpdate);
        document.removeEventListener('scroll', this.handleDropdownPositionUpdate, true);

        if (this.dropdownState.dropdownPositionUpdateRafId !== null) {
          cancelAnimationFrame(this.dropdownState.dropdownPositionUpdateRafId);
          this.dropdownState.dropdownPositionUpdateRafId = null;
        }
      },

      handleDropdownPositionUpdate() {
        if (!this.isOpen || this.dropdownState.dropdownPositionUpdateRafId !== null) return;

        this.dropdownState.dropdownPositionUpdateRafId = requestAnimationFrame(() => {
          this.dropdownState.dropdownPositionUpdateRafId = null;
          this.updateDropdownPosition();
        });
      },

      updateDropdownPosition() {
        const inputContainerElement = this.$refs.inputContainer as HTMLElement | undefined;
        const dropdownContainerElement = this.$refs.dropdownContainer as HTMLElement | undefined;

        if (!inputContainerElement) return;

        const inputRect = inputContainerElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        const availableSpaceBelow = Math.max(0, viewportHeight - inputRect.bottom);
        const availableSpaceAbove = Math.max(0, inputRect.top);

        const nextIsDropdownUp =
          availableSpaceBelow < this.dropdownMaxHeight && availableSpaceAbove > availableSpaceBelow;

        const availableDropdownSpace = nextIsDropdownUp ? availableSpaceAbove : availableSpaceBelow;
        const nextDropdownListMaxHeight = Math.min(this.dropdownMaxHeight, availableDropdownSpace);

        if (dropdownContainerElement) {
          dropdownContainerElement.style.setProperty(
            '--hierarchical-multiselect-dropdown-max-height',
            `${nextDropdownListMaxHeight}px`,
          );
        }

        if (this.dropdownState.isDropdownUp !== nextIsDropdownUp) {
          this.dropdownState.isDropdownUp = nextIsDropdownUp;
        }
      },

      toggleExpanded(item: TProcessedTreeOption) {
        const key = this.getItemKey(item);
        if (this.expandedItems.has(key)) {
          this.expandedItems.delete(key);
        } else {
          this.expandedItems.add(key);
        }
      },

      getItemKey(item: TProcessedTreeOption) {
        return `${item.value}-${item.level}`;
      },

      getOptionByValue(id: string | number): TProcessedTreeOption | undefined {
        return this.flatMapByValue[String(id)];
      },

      getVisibleIndexByValue(value: string | number): number {
        return this.getVisibleOptions.findIndex((option) => option.value === value);
      },

      selectVisibleRange(startIndex: number, endIndex: number): void {
        const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
        const rangeValues: Array<string | number> = [];

        for (let index = from; index <= to; index += 1) {
          const option = this.getVisibleOptions[index];
          if (option) {
            rangeValues.push(option.value);
          }
        }

        this.selectedIds = rangeValues;
      },

      toggleSelection(item: TProcessedTreeOption, event?: MouseEvent): void {
        if (!Array.isArray(this.modelValue)) {
          this.selectedIds = this.selectedIds[0] === item.value ? [] : [item.value];
        } else {
          if (event?.shiftKey && !this.isSingleMode) {
            const anchorValue = this.shiftRangeAnchorValue ?? this.selectedIds[0];
            if (anchorValue === undefined) {
              this.selectedIds = [item.value];
              this.shiftRangeAnchorValue = item.value;
              this.updateModelValue();
              return;
            }

            if (anchorValue === item.value && this.getSelectedValueSet.has(item.value)) {
              this.selectedIds = [anchorValue];
              this.updateModelValue();
              return;
            }

            const anchorIndex = this.getVisibleIndexByValue(anchorValue);
            const currentIndex = this.getVisibleIndexByValue(item.value);

            if (anchorIndex === -1 || currentIndex === -1) {
              return;
            }

            this.selectVisibleRange(anchorIndex, currentIndex);
            this.updateModelValue();
            return;
          }

          this.shiftRangeAnchorValue = item.value;
          const has = this.getSelectedValueSet.has(item.value);
          this.selectedIds = has
            ? this.selectedIds.filter((id) => id !== item.value)
            : [...this.selectedIds, item.value];

          if (this.selectedIds.length === 0) {
            this.shiftRangeAnchorValue = null;
          }
        }
        this.updateModelValue();
      },

      filterLevel(items: TProcessedTreeOption[], query: string): TProcessedTreeOption[] {
        return items
          .map((item) => {
            const hasMatchedIds = this.matchedSearchIds !== null;
            const matchesQuery = hasMatchedIds
              ? this.matchedSearchIds?.has(item.value)
              : item.label.toLowerCase().includes(query);
            const hasMatchingChildren = item.hasChildren && this.filterLevel(item.children, query).length > 0;

            if (matchesQuery || hasMatchingChildren) {
              return {
                ...item,
                hasChildren: hasMatchingChildren,
                children: hasMatchingChildren ? this.filterLevel(item.children, query) : [],
              };
            }
            return null;
          })
          .filter(Boolean) as TProcessedTreeOption[];
      },

      expandItemWithDescendants(items: TProcessedTreeOption[]) {
        items.forEach((item) => {
          this.expandedItems.add(this.getItemKey(item));

          if (item.hasChildren && item.children.length > 0) {
            this.expandItemWithDescendants(item.children);
          }
        });
      },

      collapseItemWithDescendants(items: TProcessedTreeOption[]) {
        items.forEach((item) => {
          this.expandedItems.delete(this.getItemKey(item));
          this.collapseItemWithDescendants(item.children);
        });
      },

      addClickOutsideListener() {
        document.addEventListener('mousedown', this.handleClickOutside);
      },

      removeClickOutsideListener() {
        document.removeEventListener('mousedown', this.handleClickOutside);
      },

      handleClickOutside(event: Event) {
        if (!this.$el.contains(event.target)) {
          this.isOpen = false;
        }
      },

      clearValue() {
        this.selectedIds = [];
        this.shiftRangeAnchorValue = null;
        this.updateModelValue();
      },

      clearSearchQuery() {
        this.searchQuery = '';
      },

      updateModelValue() {
        this.$emit(
          'update:modelValue',
          Array.isArray(this.modelValue) ? [...this.selectedIds] : this.selectedIds[0] ?? '',
        );
      },

      measureElement(el: Element | ComponentPublicInstance | null) {
        if (!el) {
          return;
        }
        let target: HTMLElement | null = null;

        if (el instanceof HTMLElement) {
          target = el;
        } else if ('$el' in el && el.$el instanceof HTMLElement) {
          target = el.$el as HTMLElement;
        }

        if (!target) return;
        const key = target.dataset.key;
        if (key) {
          this.measuredHeightsMap.set(key, target.getBoundingClientRect().height);
        }

        this.rowVirtualizer?.measureElement(target);
      },

      openContextMenu(item: TProcessedTreeOption, event: MouseEvent) {
        if (this.getIsCombinedModalMode) {
          this.localContextMenu.selectedItem = item;
          this.openCombinedContextMenuAtClickPoint(event);
          return;
        }
        if (!this.getIsShowLocalContextMenu) {
          return;
        }
        this.localContextMenu.selectedItem = item;
        this.openContextMenuInTheRightPosition(this.localContextMenu, event.currentTarget as HTMLElement | undefined);
        this.closeGlobalContextMenu();
      },

      handleActionsButtonClick() {
        this.openContextMenuInTheRightPosition(
          this.globalContextMenu,
          this.$refs.globalContextMenuActions as HTMLElement | undefined,
        );
        this.closeLocalContextMenu();
      },

      openCombinedContextMenuAtClickPoint(event: MouseEvent) {
        this.combinedContextMenu.position = {
          left: event.clientX + window.scrollX,
          top: event.clientY + window.scrollY,
        };
        this.combinedContextMenu.isVisible = true;
        this.closeGlobalContextMenu();
        this.localContextMenu.isVisible = false;
      },

      closeCombinedContextMenu() {
        this.combinedContextMenu.isVisible = false;
        this.closeLocalContextMenu();
      },

      closeActionUi() {
        this.closeGlobalContextMenu();
        this.closeLocalContextMenu();
        if (this.getIsCombinedModalMode) {
          this.combinedContextMenu.isVisible = false;
        }
      },

      openContextMenuInTheRightPosition(context: ContextMenu, target: HTMLElement | undefined) {
        if (!target) return;

        const rect = target.getBoundingClientRect();

        this.openContextMenuByPosition(context, {
          left: rect.right + +window.scrollX,
          top: rect.top + +window.scrollY,
        });
      },

      openContextMenuByPosition(context: ContextMenu, position: ContextMenu['position']) {
        context.position = position;
        context.isVisible = true;
      },

      closeLocalContextMenu() {
        this.localContextMenu.isVisible = false;
        this.localContextMenu.selectedItem = null;
      },

      closeGlobalContextMenu() {
        this.globalContextMenu.isVisible = false;
      },

      getAllDescendants(item: TProcessedTreeOption): TProcessedTreeOption[] {
        const descendants: TProcessedTreeOption[] = [item];

        if (item.hasChildren && item.children.length > 0) {
          item.children.forEach((child) => {
            descendants.push(...this.getAllDescendants(child));
          });
        }

        return descendants;
      },

      selectItem(item: TProcessedTreeOption) {
        if (!this.getSelectedValueSet.has(item.value)) {
          this.selectedIds = [...this.selectedIds, item.value];
        }
      },

      unselectItem(item: TProcessedTreeOption) {
        this.selectedIds = this.selectedIds.filter((id) => id !== item.value);
      },

      selectItemWithDescendants(item: TProcessedTreeOption) {
        this.getAllDescendants(item).forEach(this.selectItem);
      },

      selectAllDescendants(item: TProcessedTreeOption) {
        item.children.forEach(this.selectItemWithDescendants);
      },

      selectAllDescendantsByLevel(item: TProcessedTreeOption) {
        item.children.forEach(this.selectItem);
      },

      selectAll() {
        const ids: (string | number)[] = [];
        const seen = new Set<string | number>();
        const collect = (items: TProcessedTreeOption[]) => {
          items.forEach((item) => {
            if (!seen.has(item.value)) {
              seen.add(item.value);
              ids.push(item.value);
            }
            if (item.children?.length) collect(item.children);
          });
        };
        collect(this.filteredOptions);
        this.selectedIds = ids;
        this.updateModelValue();
      },

      unselectItemWithDescendants(item: TProcessedTreeOption) {
        this.getAllDescendants(item).forEach(this.unselectItem);
      },

      unselectAllDescendants(item: TProcessedTreeOption) {
        item.children.forEach(this.unselectItemWithDescendants);
      },

      unselectAllDescendantsByLevel(item: TProcessedTreeOption) {
        item.children.forEach(this.unselectItem);
      },

      unselectAll() {
        this.selectedIds = [];
        this.shiftRangeAnchorValue = null;
      },

      handleLocalContextMenuAction(action: LocalContextMenuActions) {
        if (this.localContextMenu.selectedItem) {
          switch (action) {
            case LocalContextMenuActions.SELECT_ALL:
              this.selectItemWithDescendants(this.localContextMenu.selectedItem);
              break;
            case LocalContextMenuActions.UNSELECT_ALL:
              this.unselectItemWithDescendants(this.localContextMenu.selectedItem);
              break;
            case LocalContextMenuActions.EXPAND_ALL:
              this.expandedItems.add(this.getItemKey(this.localContextMenu.selectedItem));
              this.expandItemWithDescendants(this.localContextMenu.selectedItem.children);
              break;
            case LocalContextMenuActions.COLLAPSE_ALL:
              this.expandedItems.delete(this.getItemKey(this.localContextMenu.selectedItem));
              this.collapseItemWithDescendants(this.localContextMenu.selectedItem.children);
              break;
            case LocalContextMenuActions.SELECT_ALL_CHILDREN:
              this.selectAllDescendants(this.localContextMenu.selectedItem);
              break;
            case LocalContextMenuActions.UNSELECT_ALL_CHILDREN:
              this.unselectAllDescendants(this.localContextMenu.selectedItem);
              break;
            case LocalContextMenuActions.SELECT_ALL_CHILDREN_BY_LEVEL:
              this.selectAllDescendantsByLevel(this.localContextMenu.selectedItem);
              break;
            case LocalContextMenuActions.UNSELECT_ALL_CHILDREN_BY_LEVEL:
              this.unselectAllDescendantsByLevel(this.localContextMenu.selectedItem);
              break;
          }
        }
        this.closeActionUi();
        this.updateModelValue();
      },

      handleGlobalContextMenuAction(action: GlobalContextMenuActions) {
        switch (action) {
          case GlobalContextMenuActions.SELECT_ALL:
            this.selectAll();
            break;
          case GlobalContextMenuActions.UNSELECT_ALL:
            this.unselectAll();
            break;
          case GlobalContextMenuActions.EXPAND_ALL:
            this.expandItemWithDescendants(this.filteredOptions);
            break;
          case GlobalContextMenuActions.COLLAPSE_ALL:
            this.collapseItemWithDescendants(this.filteredOptions);
            break;
        }
        this.closeActionUi();
        this.updateModelValue();
      },

      processItemsByLevel(
        items: TProcessedTreeOption[],
        targetLevel: number,
        action: (item: TProcessedTreeOption) => void,
      ) {
        items.forEach((item) => {
          if (item.level === targetLevel) {
            action(item);
            return;
          }

          if (item.children && item.children.length > 0) {
            this.processItemsByLevel(item.children, targetLevel, action);
          }
        });
      },

      selectItemsByLevel(items: TProcessedTreeOption[], targetLevel: number) {
        this.processItemsByLevel(items, targetLevel, (item) => this.selectItem(item));
      },

      unselectItemsByLevel(items: TProcessedTreeOption[], targetLevel: number) {
        this.processItemsByLevel(items, targetLevel, (item) => this.unselectItem(item));
      },

      getLevelsCount(items: TProcessedTreeOption[]): number {
        if (!items || items.length === 0) {
          return 0;
        }

        let maxLevel = 0;

        items.forEach((item) => {
          const currentLevel = item.level || 1;
          maxLevel = Math.max(maxLevel, currentLevel);

          if (item.children && item.children.length > 0) {
            const childrenMaxLevel = this.getLevelsCount(item.children);
            maxLevel = Math.max(maxLevel, childrenMaxLevel);
          }
        });

        return maxLevel;
      },

      setDefaultValue() {
        const ids = Array.isArray(this.modelValue)
          ? [...this.modelValue]
          : this.modelValue !== undefined && this.modelValue !== null && this.modelValue !== ''
            ? [this.modelValue]
            : [];
        if (this.selectedIds.length !== ids.length || ids.some((id, i) => this.selectedIds[i] !== id)) {
          this.selectedIds = ids;
        }
        if (ids.length === 0) {
          this.shiftRangeAnchorValue = null;
          return;
        }

        if (this.shiftRangeAnchorValue === null || !ids.includes(this.shiftRangeAnchorValue)) {
          this.shiftRangeAnchorValue = ids[0];
        }
      },
    },
    computed: {
      getSelectedValueSet(): Set<string | number> {
        return new Set(this.selectedIds);
      },
      getValue(): TSelectOption<string | number>[] | { length: number; 0?: TSelectOption<string | number> } {
        const count = this.selectedIds.length;

        if (count === 0) {
          return [];
        }

        if (count === 1) {
          const option = this.getOptionByValue(this.selectedIds[0]);
          return option ? [{ value: option.value, label: option.label }] : [];
        }

        return { length: count };
      },
      getPreparedOptions(): TTreeOption[] {
        return this.prepareOptions(this.options);
      },
      getAdditionalMenu(): TAdditionalMenuConfig[] {
        if (this.menuMode === MenuModes.ACTION_ONLY || this.menuMode === MenuModes.NONE) return [];

        const levelsCount = this.getLevelsCount(this.flattenedOptions);

        return Array.from({ length: levelsCount }, (_, index) => {
          const level = index + 1;

          const levelEventLabel = this.levelEventsLabels[index] ?? level;

          return [
            {
              action: () => {
                this.selectItemsByLevel(this.flattenedOptions, level);
                this.closeActionUi();
                this.updateModelValue();
              },
              label: `Отметить уровень: ${levelEventLabel}`,
            },
            {
              action: () => {
                this.unselectItemsByLevel(this.flattenedOptions, level);
                this.closeActionUi();
                this.updateModelValue();
              },
              label: `Снять отметку с уровня: ${levelEventLabel}`,
            },
          ];
        }).flat();
      },
      getIsFlatTree() {
        return this.flattenedOptions.every((item) => !item.hasChildren);
      },
      getIsCombinedModalMode(): boolean {
        return this.menuMode === MenuModes.COMBINED_MODAL;
      },
      getIsShowActionButton(): boolean {
        return this.menuMode === MenuModes.ACTION_ONLY || this.menuMode === MenuModes.ACTION_AND_LOCAL;
      },
      getIsShowGlobalContextMenu(): boolean {
        return this.menuMode !== MenuModes.NONE && this.menuMode !== MenuModes.COMBINED_MODAL;
      },
      getIsShowLocalContextMenu(): boolean {
        return this.menuMode === MenuModes.ACTION_AND_LOCAL;
      },
      getIsShowChildrenActions(): boolean {
        return !this.getIsFlatTree && Boolean(this.localContextMenu.selectedItem?.children?.length);
      },
      getEmptyStateText(): string {
        return this.searchQuery.trim() === '' ? this.noOptionsText : this.noResultsText;
      },
      getVisibleOptions(): TProcessedTreeOption[] {
        return this.buildVisibleOptions(this.filteredOptions);
      },
      getVirtualRows() {
        return this.rowVirtualizer?.getVirtualItems() ?? [];
      },
      getTotalSize(): string {
        return `${this.rowVirtualizer?.getTotalSize() ?? 0}px`;
      },
    },
    watch: {
      isOpen: {
        async handler(isOpen: boolean) {
          if (!isOpen) {
            this.removeDropdownPositionListeners();
            this.clearSearchQuery();
            return;
          }

          await nextTick();
          (this.$refs.searchInput as HTMLInputElement)?.focus();
          this.updateDropdownPosition();
          this.removeDropdownPositionListeners();
          this.addDropdownPositionListeners();
        },
      },
      searchQuery: {
        handler(newQuery: string) {
          if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
          }

          this.searchTimeout = window.setTimeout(() => {
            this.runSearch(newQuery);
          }, SEARCH_DEBOUNCE_DELAY_MS);
        },
      },

      modelValue: {
        handler() {
          this.setDefaultValue();
        },
        immediate: true,
      },

      getPreparedOptions: {
        handler() {
          this.setDefaultValue();
        },
      },

      options: {
        handler() {
          this.processOptions();
          this.setDefaultValue();
        },
        immediate: true,
      },

      getVisibleOptions: {
        handler(newValue: TProcessedTreeOption[]) {
          this.visibleOptionKeys = newValue.map((item) => this.getItemKey(item));
          this.virtualizerCount = newValue.length;
        },
        immediate: true,
      },
    },
  });
